/**
 * Lucky draw wheel — SVG + inner <g> rotation (no CSS transform on the node).
 * Gates when demo_mode is off: login → email subscribe → Instagram handle → spin.
 * App Proxy: /apps/lucky-draw/status|subscribe|spin
 */
(() => {
  const roots = document.querySelectorAll('[data-lucky-draw]');
  if (!roots.length) return;

  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const moneyFmt = (amount, currency) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
      }).format(Number(amount) || 0);
    } catch (_) {
      return `$${(Number(amount) || 0).toFixed(2)}`;
    }
  };

  const escapeHtml = (str) =>
    String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const polar = (cx, cy, r, deg) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  };

  const normalizeIns = (raw) => {
    let value = String(raw || '').trim();
    value = value.replace(/^https?:\/\/(www\.)?instagram\.com\//i, '');
    value = value.replace(/[/?#].*$/, '');
    value = value.replace(/^@/, '').trim();
    return value.slice(0, 64);
  };

  const fetchJson = async (url, options = {}) => {
    const res = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
      ...options,
    });
    const text = await res.text();
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch (_) {
      // App Proxy down / wrong tunnel usually returns a full theme HTML 500 page.
      if (res.status >= 500 || /^\s*</.test(text)) {
        throw new Error(
          'Lucky Draw API unreachable (App Proxy/tunnel). Keep npm run_dev running, deploy the current tunnel URL, and open the Freniland app once in Admin.'
        );
      }
      throw new Error(text.slice(0, 160) || `HTTP ${res.status}`);
    }
    if (!res.ok && (!data || data.ok === false)) {
      const err = new Error((data && data.error) || `HTTP ${res.status}`);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  };

  roots.forEach((root) => {
    if (root.dataset.ldReady === 'true') return;
    root.dataset.ldReady = 'true';

    let prizes = [];
    try {
      prizes = JSON.parse(root.getAttribute('data-prizes') || '[]');
    } catch (_) {
      prizes = [];
    }
    prizes = prizes.filter((p) => p && (p.name || p.short));

    const currency = root.getAttribute('data-currency') || 'USD';
    const demoMode = root.getAttribute('data-demo-mode') === 'true';
    const layout = root.getAttribute('data-layout') || 'classic';
    const softLayout = layout === 'soft';
    const apiBase = (root.getAttribute('data-api-base') || '/apps/lucky-draw').replace(/\/$/, '');
    const useApi = !demoMode && !root.hasAttribute('data-design-mode');
    const loginUrl = root.getAttribute('data-login-url') || '/account/login';
    const insStorageKey = root.getAttribute('data-ins-storage-key') || 'ld-ins';
    const softPalette = ['#b2c2a3', '#f7f1e4', '#f2d5b5', '#f9e8b3', '#e8c9a8'];
    const classicPalette = ['#c8d5c3', '#f3ebe0', '#e8c9a8', '#efe6d2', '#d9c4a5'];
    const wheelPalette = softLayout ? softPalette : classicPalette;

    // Liquid session is source of truth for "is the storefront customer logged in".
    // App Proxy status can briefly report loggedIn:false (esp. new Customer Accounts);
    // never demote a Liquid-authenticated shopper back to guest or they'll bounce to /account.
    const liquidLoggedIn = root.getAttribute('data-logged-in') === 'true';
    let loggedIn = liquidLoggedIn;
    let subscribed = root.getAttribute('data-subscribed') === 'true';
    let instagramHandle = '';
    try {
      instagramHandle = normalizeIns(window.localStorage.getItem(insStorageKey) || '');
    } catch (_) {
      instagramHandle = '';
    }

    const accountLabel = root.getAttribute('data-account-label') || '';
    const emailLabel = root.getAttribute('data-email-label') || '—';

    const els = {
      wheel: root.querySelector('[data-ld-wheel]'),
      hub: root.querySelector('[data-ld-hub]'),
      pool: root.querySelector('[data-ld-pool]'),
      caption: root.querySelector('[data-ld-caption]'),
      cta: root.querySelector('[data-ld-cta]'),
      note: root.querySelector('[data-ld-note]'),
      ticket: root.querySelector('[data-ld-ticket]'),
      tName: root.querySelector('[data-ld-t-name]'),
      tValue: root.querySelector('[data-ld-t-value]'),
      lsAccount: root.querySelector('[data-ld-ls-account]'),
      lsEmail: root.querySelector('[data-ld-ls-email]'),
      lsSpins: root.querySelector('[data-ld-ls-spins]'),
      gate: root.querySelector('[data-ld-gate]'),
      gateSubscribe: root.querySelector('[data-ld-gate-subscribe]'),
      gateIns: root.querySelector('[data-ld-gate-ins]'),
      subscribeEmail: root.querySelector('[data-ld-subscribe-email]'),
      subscribeBtn: root.querySelector('[data-ld-subscribe]'),
      insFollow: root.querySelector('[data-ld-ins-follow]'),
      insReceipt: root.querySelector('[data-ld-ins-receipt]'),
      insInput: root.querySelector('[data-ld-ins-input]'),
      insConfirm: root.querySelector('[data-ld-ins-confirm]'),
    };

    const labels = {
      accountGuest: root.getAttribute('data-label-account-guest') || 'Not signed in',
      emailEmpty: root.getAttribute('data-label-email-empty') || '—',
      spinsZero: root.getAttribute('data-label-spins-zero') || '0',
      spinsOne: root.getAttribute('data-label-spins-one') || '1',
      ctaLogin: root.getAttribute('data-cta-login') || 'Log in to spin',
      ctaSubscribe: root.getAttribute('data-cta-subscribe') || 'Subscribe & continue',
      ctaIns: root.getAttribute('data-cta-ins') || 'Confirm & continue',
      ctaSpin: root.getAttribute('data-cta-spin') || 'Spin Now',
      ctaDone: root.getAttribute('data-cta-done') || 'Prize reserved',
      hubSpin: root.getAttribute('data-hub-spin') || 'Spin',
      hubBusy: root.getAttribute('data-hub-busy') || '…',
      noteDefault: root.getAttribute('data-note-default') || '',
      noteDone: root.getAttribute('data-note-done') || 'Your prize is reserved.',
      noteLogin:
        root.getAttribute('data-note-login') ||
        'Log in, then tap Shop now and reopen this page to continue.',
      noteSubscribe: root.getAttribute('data-note-subscribe') || 'Subscribe to emails to unlock your spin.',
      noteSubscribeEmail:
        root.getAttribute('data-note-subscribe-email') || 'Enter your email to subscribe.',
      noteIns: root.getAttribute('data-note-ins') || 'Enter your Instagram username to continue.',
      captionTpl:
        root.getAttribute('data-caption-tpl') || '%count% prizes · odds vary · one spin per account',
      apiError: 'Lucky draw service unavailable. Try again later.',
    };

    if (!prizes.length || !els.wheel) return;

    const SEG = 360 / prizes.length;
    let rotation = -SEG / 2;
    let spinning = false;
    let used = false;
    let apiReady = !useApi;
    let busy = false;
    let rotator = null;

    const weightTotal = () => prizes.reduce((s, p) => s + (Number(p.weight) || 0), 0);

    const drawIndex = () => {
      const total = weightTotal() || prizes.length;
      let r = Math.random() * total;
      for (let i = 0; i < prizes.length; i += 1) {
        r -= Number(prizes[i].weight) || 0;
        if (r <= 0) return i;
      }
      return prizes.length - 1;
    };

    const findPrizeIndex = (won) => {
      if (!won) return -1;
      const id = String(won.id || '');
      const name = String(won.name || '').toLowerCase();
      let i = prizes.findIndex((p) => String(p.id) === id);
      if (i >= 0) return i;
      i = prizes.findIndex((p) => String(p.name || '').toLowerCase() === name);
      if (i >= 0) return i;
      i = prizes.findIndex((p) => {
        const a = String(p.name || '').toLowerCase();
        return a && name && (a.includes(name) || name.includes(a));
      });
      return i;
    };

    const setLs = (el, text, cls) => {
      if (!el) return;
      el.textContent = text;
      el.className = `lucky-draw__ls-value${cls ? ` ${cls}` : ''}`;
    };

    const hasIns = () => Boolean(normalizeIns(instagramHandle));

    /** login → subscribe → ins → ready */
    const nextGate = () => {
      if (used) return 'done';
      if (!useApi) return 'ready';
      if (!loggedIn) return 'login';
      if (!subscribed) return 'subscribe';
      if (!hasIns()) return 'ins';
      return 'ready';
    };

    const canSpin = () => nextGate() === 'ready' && !spinning && (!useApi || apiReady);

    const refreshStatus = async () => {
      if (!useApi) {
        apiReady = true;
        updateChrome();
        return;
      }
      try {
        const data = await fetchJson(`${apiBase}/status`);
        apiReady = true;
        applyStatus(data);
      } catch (err) {
        apiReady = false;
        console.warn('[lucky-draw] status failed', err);
        updateChrome();
      }
    };

    const goLogin = () => {
      // Already logged in on this page — visiting login again sends Shopify to account profile.
      if (liquidLoggedIn || loggedIn) {
        if (els.note) {
          els.note.textContent = 'Already signed in — refreshing…';
        }
        window.location.reload();
        return;
      }
      // Do NOT use /customer_authentication/login?return_to=… on this shop — it throws
      // redirect_uri mismatch. account_login_url lands on Orders; customer returns via Shop now.
      try {
        const path =
          root.getAttribute('data-login-return-path') ||
          window.location.pathname ||
          '/pages/lucky-draw';
        window.sessionStorage.setItem('ld-return-path', path);
      } catch (_) {
        /* ignore */
      }
      window.location.assign(loginUrl || '/account/login');
    };

    const syncInsContinue = () => {
      if (!els.insConfirm) return;
      const filled = Boolean(normalizeIns(els.insInput ? els.insInput.value : ''));
      els.insConfirm.disabled = !filled;
    };

    const resetInsReceipt = (forceShow) => {
      if (!els.insReceipt) return;
      const show = forceShow || Boolean(instagramHandle);
      els.insReceipt.hidden = !show;
      if (els.insInput && instagramHandle) els.insInput.value = instagramHandle;
      syncInsContinue();
    };

    const showGate = (which) => {
      if (!els.gate) return;
      const need = which === 'subscribe' || which === 'ins';
      els.gate.hidden = !need;
      document.documentElement.classList.toggle('lucky-draw-modal-open', need);
      if (els.gateSubscribe) els.gateSubscribe.hidden = which !== 'subscribe';
      if (els.gateIns) els.gateIns.hidden = which !== 'ins';
      if (which === 'subscribe' && els.subscribeEmail) {
        if (!els.subscribeEmail.value && emailLabel && emailLabel !== '—') {
          els.subscribeEmail.value = emailLabel;
        }
        window.setTimeout(() => {
          if (els.subscribeEmail && !els.subscribeEmail.readOnly) els.subscribeEmail.focus();
        }, 0);
      }
      if (which === 'ins') {
        // First show Follow CTA; username form appears after Follow click (or if already filled).
        resetInsReceipt(Boolean(instagramHandle));
        if (instagramHandle && els.insInput) {
          window.setTimeout(() => els.insInput.focus(), 0);
        }
      }
    };

    const updateChrome = () => {
      const gate = nextGate();

      if (loggedIn) {
        setLs(els.lsAccount, accountLabel || 'Signed in', 'is-ok');
        setLs(els.lsEmail, emailLabel || labels.emailEmpty, emailLabel ? 'is-ok' : 'is-muted');
      } else {
        setLs(els.lsAccount, labels.accountGuest, 'is-muted');
        setLs(els.lsEmail, labels.emailEmpty, 'is-muted');
      }
      setLs(
        els.lsSpins,
        used ? labels.spinsZero : gate === 'ready' || loggedIn ? labels.spinsOne : labels.spinsZero,
        used || gate === 'login' ? 'is-muted' : 'is-ok'
      );

      if (els.cta) {
        if (gate === 'done') {
          els.cta.textContent = labels.ctaDone;
          els.cta.disabled = true;
        } else if (gate === 'login') {
          els.cta.textContent = labels.ctaLogin;
          els.cta.disabled = false;
        } else if (gate === 'subscribe') {
          els.cta.textContent = labels.ctaSubscribe;
          els.cta.disabled = busy;
        } else if (gate === 'ins') {
          els.cta.textContent = labels.ctaIns;
          els.cta.disabled = busy;
        } else {
          els.cta.textContent = labels.ctaSpin;
          els.cta.disabled = !canSpin() || busy;
        }
      }

      if (els.hub) {
        els.hub.textContent = spinning ? labels.hubBusy : labels.hubSpin;
        // Keep clickable for every unfinished gate (disabled buttons ignore clicks).
        const hubClickable = gate !== 'done' && !spinning && !busy;
        els.hub.disabled = !hubClickable;
        els.hub.classList.toggle('is-idle', hubClickable && gate === 'ready');
      }

      if (els.note) {
        if (gate === 'done') els.note.textContent = labels.noteDone;
        else if (gate === 'login') els.note.textContent = labels.noteLogin;
        else if (gate === 'subscribe') els.note.textContent = labels.noteSubscribe;
        else if (gate === 'ins') els.note.textContent = labels.noteIns;
        else if (!apiReady && useApi && loggedIn) els.note.textContent = labels.apiError;
        else els.note.textContent = labels.noteDefault;
      }

      if (els.caption) {
        els.caption.textContent = labels.captionTpl.replace(/%count%/g, String(prizes.length));
      }

      // Highlight howto steps as gates complete: 1 login → 2 subscribe → 3 Instagram → 4 spun
      root.querySelectorAll('[data-ld-step]').forEach((li) => {
        const n = Number(li.getAttribute('data-ld-step'));
        let complete = false;
        if (n === 1) complete = loggedIn;
        else if (n === 2) complete = subscribed;
        else if (n === 3) complete = hasIns();
        else if (n === 4) complete = used;
        li.classList.toggle('is-done', complete);
        li.classList.remove('is-current');
      });
      const nextStep = root.querySelector('[data-ld-step]:not(.is-done)');
      if (nextStep) nextStep.classList.add('is-current');

      // Close modal when login/ready/done; open only via CTA / subscribe success.
      if (gate === 'login' || gate === 'ready' || gate === 'done') {
        showGate(null);
      }
    };

    const setAngle = (deg) => {
      rotation = deg;
      if (rotator) rotator.setAttribute('transform', `rotate(${deg} 200 200)`);
    };

    const buildWheel = () => {
      const R = 200;
      const cx = 200;
      const cy = 200;
      let body = '';

      for (let i = 0; i < prizes.length; i += 1) {
        const a0 = i * SEG;
        const a1 = a0 + SEG;
        const p0 = polar(cx, cy, R, a0);
        const p1 = polar(cx, cy, R, a1);
        const fill = wheelPalette[i % wheelPalette.length];
        const fg = softLayout ? '#4a4036' : '#3a3834';
        const stroke = softLayout ? '#c9b08a' : '#c4ad8a';

        body +=
          `<path d="M${cx} ${cy}` +
          ` L${p0[0].toFixed(2)} ${p0[1].toFixed(2)}` +
          ` A${R} ${R} 0 0 1 ${p1[0].toFixed(2)} ${p1[1].toFixed(2)}` +
          ` Z" fill="${fill}" stroke="${stroke}" stroke-width="${softLayout ? '1' : '1.2'}"/>`;

        const mid = a0 + SEG / 2;
        const flip = mid > 90 && mid < 270;
        const prize = prizes[i];
        const lines = Array.isArray(prize.short)
          ? prize.short.filter(Boolean)
          : String(prize.short || prize.name || '')
              .split(/\s+/)
              .slice(0, 2);

        if (softLayout) {
          const labelR = 118;
          const [tx, ty] = polar(cx, cy, labelR, mid);
          body += `<g transform="translate(${tx.toFixed(2)} ${ty.toFixed(2)})">`;
          const startY = lines.length > 1 ? -9 : 4;
          for (let l = 0; l < lines.length; l += 1) {
            body +=
              `<text x="0" y="${startY + l * 15}" text-anchor="middle" dominant-baseline="middle" ` +
              `font-family="Inter,Helvetica,Arial,sans-serif" font-size="11.5" font-weight="700" ` +
              `letter-spacing="0.2" fill="${fg}">${escapeHtml(String(lines[l]))}</text>`;
          }
          body += '</g>';
        } else {
          body += `<g transform="rotate(${mid} 200 200)">`;
          if (prize.icon) {
            const href = escapeHtml(prize.icon);
            body += `<image href="${href}" xlink:href="${href}" x="186" y="42" width="28" height="28"${
              flip ? ' transform="rotate(180 200 56)"' : ''
            }/>`;
          } else {
            body += `<text x="200" y="56" text-anchor="middle" font-size="18"${
              flip ? ' transform="rotate(180 200 56)"' : ''
            }>${escapeHtml(prize.emoji || '🎁')}</text>`;
          }
          if (flip) body += '<g transform="rotate(180 200 94)">';
          for (let l = 0; l < lines.length; l += 1) {
            const lineY = flip ? 79 + l * 15 : 94 + l * 15;
            body +=
              `<text x="200" y="${lineY}" text-anchor="middle" ` +
              `font-family="Inter,Helvetica,Arial,sans-serif" font-size="14" font-weight="800" ` +
              `letter-spacing="0.6" fill="${fg}">${escapeHtml(String(lines[l]).toUpperCase())}</text>`;
          }
          if (flip) body += '</g>';
          body += '</g>';
        }
      }

      body += softLayout
        ? '<circle cx="200" cy="200" r="50" fill="#c9a574"/><circle cx="200" cy="200" r="44" fill="#b88955"/>'
        : '<circle cx="200" cy="200" r="47" fill="#c4a574"/>';

      els.wheel.innerHTML =
        `<svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">` +
        `<g data-ld-rotator transform="rotate(${rotation} 200 200)">${body}</g>` +
        `</svg>`;

      els.wheel.style.transform = '';
      els.wheel.style.willChange = '';
      els.wheel.style.transition = '';
      rotator = els.wheel.querySelector('[data-ld-rotator]');
    };

    const easeOutCubic = (t) => 1 - (1 - t) ** 3;

    const spinTo = (idx, durationSec) =>
      new Promise((resolve) => {
        const from = ((rotation % 360) + 360) % 360;
        const desired = (360 - (idx * SEG + SEG / 2)) % 360;
        const delta = (desired - from + 360) % 360;
        const travel = durationSec === 0 || reduceMotion ? delta : 360 * 6 + delta;
        const to = from + travel;

        if (durationSec === 0 || reduceMotion) {
          setAngle(to);
          resolve();
          return;
        }

        const start = performance.now();
        const durationMs = durationSec * 1000;
        const tick = (now) => {
          const t = Math.min(1, (now - start) / durationMs);
          setAngle(from + travel * easeOutCubic(t));
          if (t < 1) {
            window.requestAnimationFrame(tick);
            return;
          }
          setAngle(to);
          resolve();
        };
        window.requestAnimationFrame(tick);
      });

    const renderPool = () => {
      if (!els.pool) return;
      const total = weightTotal() || 1;
      els.pool.innerHTML = prizes
        .map((p) => {
          const pct = Math.round(((Number(p.weight) || 0) / total) * 100);
          const value = p.value != null && p.value !== '' ? moneyFmt(p.value, currency) : '';
          const odds = value ? `${value} · ${pct}% odds` : `${pct}% odds`;
          const iconHtml = p.icon
            ? `<img class="lucky-draw__pool-icon" src="${escapeHtml(p.icon)}" alt="" width="28" height="28" loading="lazy">`
            : `<span class="lucky-draw__pool-emoji" aria-hidden="true">${escapeHtml(p.emoji || '🎁')}</span>`;
          return `<div class="lucky-draw__pool-item" title="${escapeHtml(p.name)}">
            ${iconHtml}
            <span class="lucky-draw__pool-info">
              <span class="lucky-draw__pool-name">${escapeHtml(p.name)}</span>
              <span class="lucky-draw__pool-odds">${escapeHtml(odds)}</span>
            </span>
          </div>`;
        })
        .join('');
    };

    const showTicket = (prize) => {
      if (!els.ticket || !prize) return;
      if (els.tName) els.tName.textContent = prize.name || '';
      if (els.tValue) {
        if (prize.wonAt) {
          try {
            els.tValue.textContent = `Won ${new Date(prize.wonAt).toLocaleString()}`;
          } catch (_) {
            els.tValue.textContent = '';
          }
        } else if (prize.value != null && prize.value !== '') {
          els.tValue.textContent = `Worth ${moneyFmt(prize.value, currency)}`;
        } else {
          els.tValue.textContent = '';
        }
      }
      els.ticket.hidden = false;
    };

    const applyStatus = (data) => {
      if (!data) return;
      if (typeof data.loggedIn === 'boolean') {
        loggedIn = Boolean(data.loggedIn) || liquidLoggedIn;
      }
      if (typeof data.subscribed === 'boolean') {
        // Upgrade from API; keep Liquid true if already subscribed in theme render.
        subscribed = Boolean(data.subscribed) || subscribed;
      }
      used = Boolean(data.used);
      if (data.instagramHandle) {
        instagramHandle = normalizeIns(data.instagramHandle);
        try {
          window.localStorage.setItem(insStorageKey, instagramHandle);
        } catch (_) {
          /* ignore */
        }
      }
      if (data.prize && data.prize.name) {
        showTicket(data.prize);
        const idx = findPrizeIndex(data.prize);
        if (idx >= 0) {
          const desired = (360 - (idx * SEG + SEG / 2)) % 360;
          setAngle(desired);
        }
      }
      updateChrome();
    };

    const loadStatus = async () => {
      if (!useApi) {
        apiReady = true;
        updateChrome();
        return;
      }
      try {
        const data = await fetchJson(`${apiBase}/status`);
        apiReady = true;
        applyStatus(data);
      } catch (err) {
        apiReady = false;
        if (els.note) els.note.textContent = labels.apiError;
        console.warn('[lucky-draw] status failed', err);
        updateChrome();
      }
    };

    const readSubscribeEmail = () => {
      const raw = els.subscribeEmail ? String(els.subscribeEmail.value || '').trim() : '';
      return raw;
    };

    const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    const doSubscribe = async () => {
      if (busy || !useApi) return;
      const email = readSubscribeEmail();
      if (!email || !isValidEmail(email)) {
        if (els.note) els.note.textContent = labels.noteSubscribeEmail;
        if (els.subscribeEmail) els.subscribeEmail.focus();
        return;
      }
      busy = true;
      updateChrome();
      try {
        // Shopify: opt the logged-in Customer into email marketing (Admin consent API).
        // Email field is the account address the shopper confirms before opt-in.
        await fetchJson(`${apiBase}/subscribe`, {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
        subscribed = true;
        updateChrome();
        // Subscribe modal → Instagram modal (sequential).
        showGate(hasIns() ? null : 'ins');
        if (hasIns()) await runSpin();
      } catch (err) {
        if (err && (err.status === 401 || (err.data && err.data.code === 'NOT_LOGGED_IN'))) {
          showGate(null);
          if (liquidLoggedIn) {
            if (els.note) {
              els.note.textContent = 'Login session not synced yet. Refresh this page, then continue.';
            }
          } else {
            goLogin();
          }
        } else if (els.note) {
          els.note.textContent = (err && err.message) || labels.apiError;
        }
      } finally {
        busy = false;
        updateChrome();
      }
    };

    const confirmIns = () => {
      const value = normalizeIns(els.insInput ? els.insInput.value : '');
      if (!value) {
        if (els.note) els.note.textContent = labels.noteIns;
        if (els.insInput) els.insInput.focus();
        return false;
      }
      instagramHandle = value;
      try {
        window.localStorage.setItem(insStorageKey, value);
      } catch (_) {
        /* ignore */
      }
      updateChrome();
      return true;
    };

    const runSpin = async () => {
      if (!canSpin()) return;

      spinning = true;
      updateChrome();

      try {
        let idx;
        let wonPrize = null;

        if (useApi) {
          idx = drawIndex();
          const prizeId = prizes[idx] && prizes[idx].id;
          const data = await fetchJson(`${apiBase}/spin`, {
            method: 'POST',
            body: JSON.stringify({
              prizeId,
              instagramHandle,
            }),
          });
          wonPrize = data.prize || null;
          const matched = findPrizeIndex(wonPrize);
          if (matched >= 0) idx = matched;
        } else {
          idx = drawIndex();
          wonPrize = prizes[idx];
        }

        await spinTo(idx, reduceMotion ? 0 : 4.2);
        spinning = false;
        used = true;
        showTicket(
          wonPrize && wonPrize.name
            ? wonPrize
            : {
                ...prizes[idx],
                name: (wonPrize && wonPrize.name) || prizes[idx].name,
              }
        );
        updateChrome();
        root.dispatchEvent(
          new CustomEvent('lucky-draw:won', {
            bubbles: true,
            detail: { prize: wonPrize || prizes[idx], index: idx },
          })
        );
      } catch (err) {
        spinning = false;
        if (err && err.status === 409 && err.data && err.data.prize) {
          used = true;
          showTicket(err.data.prize);
          const idx = findPrizeIndex(err.data.prize);
          if (idx >= 0) await spinTo(idx, reduceMotion ? 0 : 1.2);
        } else if (err && err.data && err.data.code === 'NEED_SUBSCRIBE') {
          subscribed = false;
        } else if (err && err.data && err.data.code === 'NEED_INSTAGRAM') {
          instagramHandle = '';
        } else if (els.note) {
          els.note.textContent = (err && err.message) || labels.apiError;
        }
        updateChrome();
      }
    };

    const advance = async () => {
      if (busy || spinning || used) return;

      // New Customer Accounts: shopify.com profile can be signed in while this storefront
      // tab is still a stale guest page. Re-check API, then reload once before login redirect.
      if (nextGate() === 'login') {
        if (els.note) els.note.textContent = 'Checking sign-in…';
        await refreshStatus();
      }

      if (nextGate() === 'login') {
        try {
          if (!window.sessionStorage.getItem('ld-session-sync')) {
            window.sessionStorage.setItem('ld-session-sync', '1');
            if (els.note) els.note.textContent = 'Refreshing to sync your login…';
            window.location.reload();
            return;
          }
        } catch (_) {
          /* ignore */
        }
        goLogin();
        return;
      }

      try {
        window.sessionStorage.removeItem('ld-session-sync');
      } catch (_) {
        /* ignore */
      }

      const gate = nextGate();
      if (gate === 'subscribe') {
        showGate('subscribe');
        return;
      }
      if (gate === 'ins') {
        showGate('ins');
        return;
      }
      if (gate === 'ready') {
        showGate(null);
        await runSpin();
      }
    };

    if (els.hub) {
      els.hub.addEventListener('click', (event) => {
        event.preventDefault();
        advance();
      });
    }

    if (els.cta) {
      els.cta.addEventListener('click', (event) => {
        event.preventDefault();
        advance();
      });
    }

    if (els.subscribeBtn) {
      els.subscribeBtn.addEventListener('click', (event) => {
        event.preventDefault();
        doSubscribe();
      });
    }

    if (els.subscribeEmail) {
      els.subscribeEmail.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          doSubscribe();
        }
      });
    }

    if (els.insFollow) {
      els.insFollow.addEventListener('click', () => {
        // Opens Instagram in a new tab via target=_blank; then reveal username form.
        if (els.insReceipt) {
          els.insReceipt.hidden = false;
          syncInsContinue();
          if (els.insInput) window.setTimeout(() => els.insInput.focus(), 0);
        }
      });
    }

    if (els.insConfirm) {
      els.insConfirm.addEventListener('click', async (event) => {
        event.preventDefault();
        if (!confirmIns()) return;
        showGate(null);
        await runSpin();
      });
    }

    if (els.insInput) {
      els.insInput.addEventListener('input', syncInsContinue);
      els.insInput.addEventListener('keydown', async (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          if (!confirmIns()) return;
          showGate(null);
          await runSpin();
        }
      });
    }

    root.querySelectorAll('[data-ld-gate-close]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        showGate(null);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && els.gate && !els.gate.hidden) {
        showGate(null);
      }
    });

    if (liquidLoggedIn) {
      try {
        window.sessionStorage.removeItem('ld-session-sync');
      } catch (_) {
        /* ignore */
      }
    }

    // Returning from shopify.com account tab / bfcache — re-read login + spin state.
    window.addEventListener('pageshow', () => {
      if (useApi) loadStatus();
    });

    buildWheel();
    renderPool();
    updateChrome();
    loadStatus();
  });
})();
